import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocation } from "wouter";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { CalendarIcon, Clock, Users, Search } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { UserProfile } from "@shared/types";
import { useToast } from "@/hooks/use-toast";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";
import { formatDate } from "@/lib/utils";
import { apiRequest } from "@/lib/fetchWithAuth";
import { ApiError } from "@/lib/api-error";
import { isMeetingTemplate } from "@/lib/meeting-template-category";

interface MeetingAgendasProps {
  user: UserProfile;
}

interface MeetingTemplate {
  id: string;
  name: string;
  type: string;
  items: MeetingAgendaItem[];
  lastUsed?: string;
}

interface ScheduledMeeting {
  id: string;
  title: string;
  type: string;
  date: string;
  location: string;
  locationId: string;
  templateId: string;
  attendees?: string[];
}

interface MeetingAgendaItem {
  title: string;
  duration: number;
  owner?: string;
  description?: string;
}

export default function MeetingAgendas({ user }: MeetingAgendasProps) {
  const { selectedLocationIds, locations } = useLocationContext();
  const { currentOrganizationId } = useOrganization();
  const [, setLocation] = useLocation();
  const [selectedTemplate, setSelectedTemplate] = useState<string>("scheduled");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState<"all" | "today" | "week" | "month" | "future">("future");
  const { toast } = useToast();
  
  // Dialog states
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [isEditTemplateDialogOpen, setIsEditTemplateDialogOpen] = useState(false);
  const [selectedTemplateForSchedule, setSelectedTemplateForSchedule] = useState<MeetingTemplate | null>(null);
  const [selectedTemplateForEdit, setSelectedTemplateForEdit] = useState<MeetingTemplate | null>(null);
  
  // Form states
  const [meetingForm, setMeetingForm] = useState({
    title: "",
    date: "",
    time: "",
    locationId: selectedLocationIds[0] || "",
    attendees: "",
    notes: ""
  });

  // New state for enhanced UI components
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState("");
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);
  const [attendeeSearchOpen, setAttendeeSearchOpen] = useState(false);
  const [attendeeSearchTerm, setAttendeeSearchTerm] = useState("");

  // AI Analysis dialog state
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [analysisTranscript, setAnalysisTranscript] = useState("");
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<string>("");

  // Get employees for attendee selection
  const { data: employees } = useQuery({
    queryKey: ['employees', currentOrganizationId || user.organizationId],
    queryFn: async () => {
      const orgId = currentOrganizationId || user.organizationId;
      return apiRequest(`/api/users?organizationId=${orgId}`);
    },
    enabled: !!(currentOrganizationId || user.organizationId),
  });

  // Filter employees based on search term
  const filteredEmployees = employees?.filter((employee: any) =>
    employee.fullName.toLowerCase().includes(attendeeSearchTerm.toLowerCase()) ||
    employee.email.toLowerCase().includes(attendeeSearchTerm.toLowerCase())
  ) || [];

  // Handler to use a template
  const handleUseTemplate = (template: MeetingTemplate) => {
    console.log('Using template:', template.name);
    setSelectedTemplateForSchedule(template);
    setMeetingForm({
      title: template.name,
      date: "",
      time: "",
      locationId: selectedLocationIds.length === 1 ? selectedLocationIds[0] : "",
      attendees: "",
      notes: ""
    });
    setIsScheduleDialogOpen(true);
  };

  // Handler to edit a template
  const handleEditTemplate = (template: MeetingTemplate) => {
    console.log('Editing template:', template.name);
    setSelectedTemplateForEdit(template);
    setIsEditTemplateDialogOpen(true);
  };

  // Handler to schedule a meeting
  const handleScheduleMeeting = async () => {
    try {
      console.log('Scheduling meeting:', meetingForm);
      console.log('Selected template:', selectedTemplateForSchedule);
      
      if (!meetingForm.locationId) {
        toast({
          title: "Location Required",
          description: "Please select a location for the meeting",
          variant: "destructive"
        });
        return;
      }

      if (!meetingForm.date || !meetingForm.time) {
        toast({
          title: "Date and Time Required",
          description: "Please select both date and time for the meeting",
          variant: "destructive"
        });
        return;
      }

      // Ensure date is in YYYY-MM-DD format
      let formattedDate = meetingForm.date;
      if (meetingForm.date.includes('/')) {
        // Convert MM/DD/YYYY to YYYY-MM-DD
        const [month, day, year] = meetingForm.date.split('/');
        formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }

      const meetingData = {
        locationId: meetingForm.locationId,
        organizationId: currentOrganizationId || user.organizationId,
        type: selectedTemplateForSchedule?.type === 'RoleBased' ? 'Level10' : 
              selectedTemplateForSchedule?.type === 'ClarityBreak' ? 'Level10' :
              selectedTemplateForSchedule?.type || "Level10",
        date: new Date(`${formattedDate}T${meetingForm.time}:00Z`).toISOString(),
        // Extended fields for storage (will be handled by the extended storage)
        title: meetingForm.title,
        location: getLocationName(meetingForm.locationId),
        templateId: selectedTemplateForSchedule?.id || "",
        attendees: meetingForm.attendees.split(',').map(a => a.trim()).filter(a => a),
        notes: meetingForm.notes,
        userId: user.id
      };

      console.log('Meeting data being sent:', meetingData);

      const result = await apiRequest('/api/meetings', {
        method: 'POST',
        data: meetingData
      });

      console.log('Meeting created:', result);

      toast({
        title: "Meeting Scheduled",
        description: `${meetingForm.title} has been scheduled for ${meetingForm.date}`
      });

      setIsScheduleDialogOpen(false);
      setSelectedTemplateForSchedule(null);
      setMeetingForm({
        title: "",
        date: "",
        time: "",
        locationId: selectedLocationIds[0] || "",
        attendees: "",
        notes: ""
      });

      // Refetch meetings
      // queryClient.invalidateQueries(['scheduled-meetings']);
    } catch (error) {
      console.error('Error scheduling meeting:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to schedule meeting",
        variant: "destructive"
      });
    }
  };

  // Get location name helper
  const getLocationName = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId);
    return location ? location.name : locationId;
  };

  // Get display title for current selection
  const getDisplayTitle = () => {
    if (selectedLocationIds.includes('all')) {
      return 'All Locations';
    } else if (selectedLocationIds.length === 1) {
      return getLocationName(selectedLocationIds[0]);
    } else {
      return `${selectedLocationIds.length} Locations Combined`;
    }
  };

  // Get meeting templates with multi-location support
  const { data: allTemplates, isLoading: isLoadingTemplates, isError: templatesFailed,
    failureReason: templatesFailure, isFetching: templatesFetching } = useQuery<MeetingTemplate[]>({
    queryKey: ['meeting-templates', selectedLocationIds, currentOrganizationId],
    queryFn: async () => {
      console.log('📋 Loading meeting templates for locations:', selectedLocationIds);
      
      // Get templates for current organization
      const orgId = currentOrganizationId || user.organizationId;
      const templates = await apiRequest(`/api/templates?organizationId=${orgId}`);
      
      // Match the Template Library's stored category, including Annual Planning.
      const meetingTemplates = templates.filter(isMeetingTemplate);
      
      console.log(`📋 Found ${meetingTemplates.length} meeting templates (universal for all organizations)`);
      return meetingTemplates;
    },
    enabled: selectedLocationIds.length > 0
  });

  // Get scheduled meetings with multi-location support
  const { data: allMeetings, isLoading: isLoadingMeetings, isError: meetingsFailed,
    failureReason: meetingsFailure, isFetching: meetingsFetching } = useQuery<ScheduledMeeting[]>({
    queryKey: ['scheduled-meetings', selectedLocationIds, currentOrganizationId],
    queryFn: async () => {
      console.log('📅 Loading meetings for locations:', selectedLocationIds);
      
      if (selectedLocationIds.length === 0) {
        return [];
      }
      
      const orgId = currentOrganizationId || user.organizationId;
      
      if (selectedLocationIds.includes('all')) {
        // Load meetings for all available locations
        const meetingsPromises = locations.map(location => 
          apiRequest(`/api/meetings?locationId=${location.id}&organizationId=${orgId}`)
        );
        
        const meetingsArrays = await Promise.all(meetingsPromises);
        const allMeetings = meetingsArrays.flat();
        
        // Remove duplicates if any
        const uniqueMeetings = Array.from(
          new Map(allMeetings.map(m => [m.id, m])).values()
        );
        
        console.log(`📅 Loaded ${uniqueMeetings.length} meetings from all locations`);
        return uniqueMeetings;
      } else if (selectedLocationIds.length === 1) {
        // Single location
        return apiRequest(`/api/meetings?locationId=${selectedLocationIds[0]}&organizationId=${orgId}`);
      } else {
        // Multi-select: fetch meetings for each location and combine
        const meetingsPromises = selectedLocationIds.map(locationId => 
          apiRequest(`/api/meetings?locationId=${locationId}&organizationId=${orgId}`)
        );
        
        const meetingsArrays = await Promise.all(meetingsPromises);
        const allMeetings = meetingsArrays.flat();
        
        // Remove duplicates if any
        const uniqueMeetings = Array.from(
          new Map(allMeetings.map(m => [m.id, m])).values()
        );
        
        console.log(`📅 Loaded ${uniqueMeetings.length} meetings from ${selectedLocationIds.length} locations`);
        return uniqueMeetings;
      }
    },
    enabled: selectedLocationIds.length > 0
  });

  // Process data
  const meetingTemplates = allTemplates || [];
  const meetings = allMeetings || [];
  
  // Filter scheduled meetings based on date filter
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Start of today
  
  const scheduledMeetings = meetings.filter(meeting => {
    const meetingDate = new Date(meeting.date);
    meetingDate.setHours(0, 0, 0, 0);
    
    switch (dateFilter) {
      case "all":
        return true;
      case "today":
        return meetingDate.getTime() === now.getTime();
      case "week":
        const weekFromNow = new Date(now);
        weekFromNow.setDate(weekFromNow.getDate() + 7);
        return meetingDate >= now && meetingDate <= weekFromNow;
      case "month":
        const monthFromNow = new Date(now);
        monthFromNow.setMonth(monthFromNow.getMonth() + 1);
        return meetingDate >= now && meetingDate <= monthFromNow;
      case "future":
      default:
        return meetingDate >= now;
    }
  });

  // Keep today's meetings in Scheduled Meetings; history starts before local
  // midnight. Invalid/missing dates cannot establish that a meeting is past.
  const pastMeetings = meetings.filter(meeting => {
    const timestamp = meeting.date ? new Date(meeting.date).getTime() : NaN;
    return Number.isFinite(timestamp) && timestamp < now.getTime();
  }).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Calculate total duration for a template
  const getTotalDuration = (items: MeetingAgendaItem[] | undefined) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((total, item) => total + (item.duration || 0), 0);
  };

  // Get template by ID
  const getTemplateById = (id: string) => {
    return meetingTemplates.find(template => template.id === id);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between mb-6">
        <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <h1 className="text-2xl font-medium">Meeting Agendas</h1>
          <span 
            className="mdi mdi-information-outline text-neutral-500 dark:text-neutral-400 cursor-help" 
            title="EOS-aligned meeting formats (Level 10, Quarterly, Annual)"
          ></span>
        </div>
        
        <div className="flex flex-wrap gap-3 items-center">
          {/* Location selection is now handled by the header LocationSelector */}
          <div className="text-sm text-gray-600">
            Viewing: {getDisplayTitle()}
          </div>
          
          <Button 
            disabled
            title="Bolt AI is coming soon"
            aria-label="AI Analysis — coming soon"
            className="bg-purple-600 hover:bg-purple-700 text-white border-0"
          >
            <span className="mdi mdi-robot-outline mr-2"></span>
            AI Analysis
          </Button>

          <Button onClick={() => setIsScheduleDialogOpen(true)}>
            <span className="mdi mdi-plus mr-2"></span>
            Schedule Meeting
          </Button>
        </div>
      </div>

      {meetingsFetching && meetingsFailure instanceof ApiError && meetingsFailure.status === 429 ? (
        <p role="status" className="mb-4 text-muted-foreground">Meetings are busy, retrying…</p>
      ) : meetingsFailed ? (
        <p role="alert" className="mb-4 text-destructive">
          Meetings could not be loaded for the selected locations. Please try again later.
        </p>
      ) : null}
      {templatesFetching && templatesFailure instanceof ApiError && templatesFailure.status === 429 ? (
        <p role="status" className="mb-4 text-muted-foreground">Templates are busy, retrying…</p>
      ) : templatesFailed ? (
        <p role="alert" className="mb-4 text-destructive">
          Templates could not be loaded. Please try again later.
        </p>
      ) : null}

      {/* Tabs */}
      <Tabs value={selectedTemplate} onValueChange={setSelectedTemplate} className="mb-6">
        <TabsList>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Meetings</TabsTrigger>
          <TabsTrigger value="past">Past Meetings</TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoadingTemplates ? (
              [...Array(4)].map((_, index) => (
                <Skeleton key={index} className="h-64 w-full" />
              ))
            ) : (
              meetingTemplates.map((template) => (
                <Card key={template.id} className="overflow-hidden">
                  <CardHeader className="p-4 pb-2">
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <Badge>{template.type}</Badge>
                    </div>
                    <div className="text-sm text-neutral-500 mt-1">
                      {getTotalDuration(template.items)} min total
                      {template.lastUsed && (
                        <span className="ml-2">• Last used: {formatDate(template.lastUsed)}</span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      {template.items && Array.isArray(template.items) && template.items.slice(0, 3).map((item, index) => (
                        <div key={index} className="flex flex-wrap gap-3 justify-between items-center">
                          <div className="flex items-center">
                            <span className="w-6 h-6 rounded-full bg-primary bg-opacity-10 text-primary text-xs flex items-center justify-center mr-2">
                              {index + 1}
                            </span>
                            <span className="text-sm">{item.title}</span>
                          </div>
                          <span className="text-xs text-neutral-500">{item.duration} min</span>
                        </div>
                      ))}
                      {template.items && Array.isArray(template.items) && template.items.length > 3 && (
                        <div className="text-center text-sm text-neutral-500">
                          +{template.items.length - 3} more items
                        </div>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="p-4 pt-2 flex justify-between">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleEditTemplate(template)}
                    >
                      <span className="mdi mdi-pencil mr-1"></span>
                      Edit
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => handleUseTemplate(template)}
                    >
                      <span className="mdi mdi-calendar-plus mr-1"></span>
                      Use Template
                    </Button>
                  </CardFooter>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Scheduled Meetings Tab */}
        <TabsContent value="scheduled" className="space-y-6">
          {/* Date Filter */}
          <div className="flex items-center space-x-2">
            <Label className="text-sm">Show:</Label>
            <Select value={dateFilter} onValueChange={(value: any) => setDateFilter(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Meetings</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Next 7 Days</SelectItem>
                <SelectItem value="month">Next 30 Days</SelectItem>
                <SelectItem value="future">Future Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {isLoadingMeetings ? (
              [...Array(3)].map((_, index) => (
                <Skeleton key={index} className="h-48 w-full" />
              ))
            ) : (
              scheduledMeetings.map((meeting) => {
                const template = getTemplateById(meeting.templateId);
                
                return (
                  <Card key={meeting.id} className="overflow-hidden">
                    <CardHeader className="p-4 pb-2">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-lg">{meeting.title}</CardTitle>
                        <Badge>{meeting.type}</Badge>
                      </div>
                      <div className="flex items-center text-neutral-600 dark:text-neutral-300 mt-2">
                        <span className="mdi mdi-calendar mr-1"></span>
                        <span className="text-sm">{formatDate(meeting.date)}</span>
                        
                        <span className="mx-2">•</span>
                        
                        <span className="mdi mdi-map-marker mr-1"></span>
                        <span className="text-sm">{meeting.location}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="mb-3">
                        <div className="text-sm font-medium mb-1">Attendees:</div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-300">
                          {meeting.attendees && Array.isArray(meeting.attendees) ? meeting.attendees.join(', ') : 'No attendees specified'}
                        </div>
                      </div>
                      
                      {template && (
                        <div>
                          <div className="text-sm font-medium mb-1">Agenda Items:</div>
                          <div className="text-sm text-neutral-600 dark:text-neutral-300">
                            {template.items && Array.isArray(template.items) ? (
                              <>{template.items.length} items • {getTotalDuration(template.items)} minutes total</>
                            ) : (
                              <>No agenda items</>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="p-4 pt-2 flex justify-end space-x-2">
                      <Button variant="outline" size="sm">
                        <span className="mdi mdi-pencil mr-1"></span>
                        Edit
                      </Button>
                      <Button 
                        size="sm"
                        onClick={() => setLocation(`/meetings/${meeting.id}/live`)}
                      >
                        <span className="mdi mdi-play mr-1"></span>
                        Start Meeting
                      </Button>
                      <Button 
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-white border-0"
                        disabled
                        title="Bolt AI is coming soon"
                        aria-label="AI Analysis — coming soon"
                      >
                        <span className="mdi mdi-robot-outline mr-1"></span>
                        AI Analysis
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })
            )}
            
            {scheduledMeetings.length === 0 && !isLoadingMeetings && !meetingsFailed && (
              <Card className="p-8 text-center md:col-span-2">
                <h3 className="text-lg font-medium mb-2">No Meetings Scheduled</h3>
                <p className="text-neutral-500 dark:text-neutral-400 mb-4">
                  There are no upcoming meetings scheduled for this location.
                </p>
                <Button onClick={() => setIsScheduleDialogOpen(true)}>
                  <span className="mdi mdi-calendar-plus mr-2"></span>
                  Schedule Meeting
                </Button>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Past Meetings Tab */}
        <TabsContent value="past" className="space-y-6">
          {isLoadingMeetings ? (
            <div role="status">Loading past meetings…</div>
          ) : meetingsFailed ? null : pastMeetings.length === 0 ? (
            <Card className="p-8 text-center">
              <h3 className="text-lg font-medium mb-2">No Past Meetings</h3>
              <p className="text-neutral-500 dark:text-neutral-400">
                There are no meetings before today for the selected locations.
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {pastMeetings.map(meeting => (
                <Card key={meeting.id} className="min-w-0 break-words">
                  <CardHeader className="p-4 pb-2">
                    <CardTitle className="text-lg">{meeting.title || 'Meeting'}</CardTitle>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                      {formatDate(meeting.date)} • {meeting.location || getLocationName(meeting.locationId)}
                    </p>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="text-sm font-medium mb-1">Attendees</div>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                      {Array.isArray(meeting.attendees) && meeting.attendees.length > 0
                        ? meeting.attendees.join(', ') : 'No attendees specified'}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* AI Analysis Dialog */}
      <Dialog open={isAnalysisOpen} onOpenChange={setIsAnalysisOpen}>
        <DialogContent className="max-w-[800px]">
          <DialogHeader>
            <DialogTitle>AI Meeting Analysis</DialogTitle>
            <DialogDescription>Paste a meeting transcript or notes and generate a summary.</DialogDescription>
          </DialogHeader>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Meeting Transcript</Label>
              <Textarea
                placeholder="Paste transcript or notes here..."
                value={analysisTranscript}
                onChange={(e) => setAnalysisTranscript(e.target.value)}
                className="min-h-[240px]"
              />
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setAnalysisTranscript(`two near-miss incidents last week.\n\nPharmacist Lee: I suggest we implement a double-check system for high-risk medications immediately.\n\nDr. Smith: Agreed. Lee, please draft the new protocol and send it to everyone by Wednesday. We'll implement it starting Monday.\n\nDr. Smith: Any other urgent matters? No? Great. Let's meet again next week. Thank you everyone.`)}
                >
                  Load Sample Healthcare Meeting
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Analysis Results</Label>
              <div className="min-h-[240px] rounded border p-3 text-sm whitespace-pre-wrap">
                {analysisLoading ? 'Analyzing...' : (analysisResult || 'Analysis results will appear here')}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAnalysisOpen(false)}>Close</Button>
            <Button
              disabled={analysisLoading || !analysisTranscript.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white border-0"
              onClick={async () => {
                try {
                  setAnalysisLoading(true);
                  setAnalysisResult("");
                  const orgId = currentOrganizationId || user.organizationId;
                  const res = await apiRequest(`/api/ai/meeting-analyze?organizationId=${orgId}`, {
                    method: 'POST',
                    data: { transcript: analysisTranscript, meetingType: 'Level10' }
                  });
                  setAnalysisResult(res.summary || 'No summary returned');
                } catch (e: any) {
                  toast({ title: 'Analysis Failed', description: e?.message || 'Network error', variant: 'destructive' });
                } finally {
                  setAnalysisLoading(false);
                }
              }}
            >
              Analyze Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule Meeting Dialog */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule Meeting</DialogTitle>
            <DialogDescription>
              {selectedTemplateForSchedule 
                ? `Scheduling a ${selectedTemplateForSchedule.name}`
                : "Create a new meeting"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Meeting Title</Label>
              <Input
                id="title"
                value={meetingForm.title}
                onChange={(e) => setMeetingForm({...meetingForm, title: e.target.value})}
                placeholder="Enter meeting title"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Enhanced Date Picker with Calendar */}
              <div className="grid gap-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !selectedDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        setSelectedDate(date);
                        if (date) {
                          setMeetingForm({
                            ...meetingForm,
                            date: format(date, "yyyy-MM-dd")
                          });
                        }
                      }}
                      disabled={(date) =>
                        date < new Date() || date < new Date("1900-01-01")
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              {/* Enhanced Time Picker */}
              <div className="grid gap-2">
                <Label>Time</Label>
                <Select
                  value={selectedTime}
                  onValueChange={(value) => {
                    setSelectedTime(value);
                    setMeetingForm({...meetingForm, time: value});
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select time">
                      <div className="flex items-center">
                        <Clock className="mr-2 h-4 w-4" />
                        {selectedTime || "Select time"}
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {/* Generate time slots from 6:00 AM to 8:00 PM */}
                    {Array.from({ length: 29 }, (_, i) => {
                      const hour = Math.floor(i / 2) + 6;
                      const minute = i % 2 === 0 ? "00" : "30";
                      const time24 = `${hour.toString().padStart(2, '0')}:${minute}`;
                      const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
                      const ampm = hour >= 12 ? 'PM' : 'AM';
                      const time12 = `${hour12}:${minute} ${ampm}`;
                      
                      return (
                        <SelectItem key={time24} value={time24}>
                          {time12}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="location">Location</Label>
              <Select
                value={meetingForm.locationId}
                onValueChange={(value) => setMeetingForm({...meetingForm, locationId: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Enhanced Attendees Selector with Employee Search */}
            <div className="grid gap-2">
              <Label>Attendees</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {selectedAttendees.map((attendee, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {attendee}
                    <button
                      onClick={() => {
                        const newAttendees = selectedAttendees.filter((_, i) => i !== index);
                        setSelectedAttendees(newAttendees);
                        setMeetingForm({
                          ...meetingForm,
                          attendees: newAttendees.join(', ')
                        });
                      }}
                      className="ml-1 text-xs hover:text-red-500"
                    >
                      ×
                    </button>
                  </Badge>
                ))}
              </div>
              
              <Popover open={attendeeSearchOpen} onOpenChange={setAttendeeSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={attendeeSearchOpen}
                    className="w-full justify-between"
                  >
                    <div className="flex items-center">
                      <Search className="mr-2 h-4 w-4" />
                      Add attendees...
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <CommandInput
                      placeholder="Search employees..."
                      value={attendeeSearchTerm}
                      onValueChange={setAttendeeSearchTerm}
                    />
                    <CommandEmpty>No employees found.</CommandEmpty>
                    <CommandGroup className="max-h-[200px] overflow-y-auto">
                      {filteredEmployees.slice(0, 20).map((employee: any) => (
                        <CommandItem
                          key={employee.id}
                          value={employee.email}
                          onSelect={() => {
                            const newAttendee = employee.email;
                            if (!selectedAttendees.includes(newAttendee)) {
                              const newAttendees = [...selectedAttendees, newAttendee];
                              setSelectedAttendees(newAttendees);
                              setMeetingForm({
                                ...meetingForm,
                                attendees: newAttendees.join(', ')
                              });
                            }
                            setAttendeeSearchOpen(false);
                            setAttendeeSearchTerm("");
                          }}
                        >
                          <div className="flex items-center space-x-2">
                            <Users className="h-4 w-4" />
                            <div>
                              <div className="font-medium">{employee.fullName}</div>
                              <div className="text-sm text-muted-foreground">{employee.email}</div>
                            </div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
              
              {/* Fallback manual input */}
              <Input
                placeholder="Or type emails manually (comma-separated)"
                value={meetingForm.attendees}
                onChange={(e) => {
                  setMeetingForm({...meetingForm, attendees: e.target.value});
                  // Update selected attendees array
                  const emails = e.target.value.split(',').map(email => email.trim()).filter(email => email);
                  setSelectedAttendees(emails);
                }}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={meetingForm.notes}
                onChange={(e) => setMeetingForm({...meetingForm, notes: e.target.value})}
                placeholder="Additional notes or agenda items"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="sticky bottom-0 bg-background pt-4 border-t">
            <Button variant="outline" onClick={() => setIsScheduleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleScheduleMeeting}>
              Schedule Meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Template Dialog */}
      <Dialog open={isEditTemplateDialogOpen} onOpenChange={setIsEditTemplateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
            <DialogDescription>
              Editing: {selectedTemplateForEdit?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              Template editing functionality coming soon. This will allow you to:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground mt-2">
              <li>Modify agenda items</li>
              <li>Adjust time allocations</li>
              <li>Add or remove sections</li>
              <li>Save custom templates</li>
            </ul>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditTemplateDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
